import React, { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  Printer,
  MessageCircle,
  CheckCircle2,
  Info,
  ArrowRight,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { DOCUMENTS, DOCUMENT_CATEGORIES } from "../data/documents";
import { whatsappUrl } from "../lib/office";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Documents() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const initialCat = params.get("cat") || "all";
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(initialCat);

  useEffect(() => {
    const c = params.get("cat");
    if (c) setCategory(c);
  }, [params]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DOCUMENTS.filter((d) => {
      if (category !== "all" && d.category !== category) return false;
      if (!q) return true;
      const data = d[lang] || d.fr;
      const hay = `${data.title} ${data.short} ${(data.items || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, category, lang]);

  const categoryLabel = (key) => {
    const c = DOCUMENT_CATEGORIES.find((x) => x.key === key);
    return c ? c[lang] || c.fr : key;
  };

  return (
    <div>
      <section className="border-b border-border-light" data-testid="documents-hero">
        <div className="container-notary py-20 md:py-24">
          <div className="overline mb-4">{t("documents.eyebrow")}</div>
          <h1 className="text-4xl md:text-5xl font-serif text-charcoal max-w-3xl">
            {t("documents.title")}
          </h1>
          <span className="divider-gold my-6 block" />
          <p className="text-lg text-charcoal-muted max-w-2xl">{t("documents.subtitle")}</p>
        </div>
      </section>

      <section className="container-notary py-12 md:py-16" data-testid="documents-filters">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-7">
            <label className="field-label">{t("documents.searchPlaceholder")}</label>
            <div className="relative">
              <Search className="absolute top-1/2 -translate-y-1/2 start-4 w-4 h-4 text-charcoal-muted" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("documents.searchPlaceholder")}
                className="field-input ps-11"
                data-testid="documents-search-input"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="field-label">{t("documents.allCategories")}</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                className="field-input h-12 rounded-sm"
                data-testid="documents-category-trigger"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_CATEGORIES.map((c) => (
                  <SelectItem key={c.key} value={c.key} data-testid={`cat-option-${c.key}`}>
                    {c[lang] || c.fr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 hidden md:block">
            <button
              type="button"
              onClick={() => window.print()}
              className="btn-outline-dark w-full h-12 no-print"
              data-testid="documents-print-btn"
            >
              <Printer className="w-4 h-4" />
              {t("common.print")}
            </button>
          </div>
        </div>

        <div className="mt-6 inline-flex items-start gap-3 bg-surface-muted border border-border-light px-4 py-3 text-sm text-charcoal-muted">
          <Info className="w-4 h-4 text-gold mt-0.5 shrink-0" />
          <div>
            <span className="font-medium text-charcoal">{t("documents.noteTitle")}: </span>
            {t("documents.note")}
          </div>
        </div>
      </section>

      <section className="container-notary pb-20 md:pb-28 print-area" data-testid="documents-list">
        {filtered.length === 0 ? (
          <div className="border border-border-light p-10 text-center text-charcoal-muted" data-testid="documents-no-results">
            {t("documents.noResults")}
          </div>
        ) : (
          <Accordion type="multiple" className="border border-border-light">
            {filtered.map((d) => {
              const data = d[lang] || d.fr;
              return (
                <AccordionItem
                  value={d.key}
                  key={d.key}
                  className="border-b border-border-light last:border-b-0"
                >
                  <AccordionTrigger
                    className="px-5 md:px-8 py-5 hover:no-underline hover:bg-base text-start"
                    data-testid={`doc-item-${d.key}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:gap-5 gap-1 w-full text-start">
                      <span className="overline text-[10px]">{categoryLabel(d.category)}</span>
                      <span className="font-serif text-lg md:text-xl text-charcoal">
                        {data.title}
                      </span>
                      <span className="hidden md:inline text-sm text-charcoal-muted md:ms-auto me-3">
                        {data.short}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 md:px-8 pb-8 bg-surface-muted/40">
                    <p className="text-sm text-charcoal-muted mb-5 md:hidden">{data.short}</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8">
                      {data.items.map((it) => (
                        <li key={it} className="flex items-start gap-3 text-sm text-charcoal">
                          <CheckCircle2 className="w-4 h-4 text-gold mt-[3px] shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 flex flex-wrap gap-3 no-print">
                      <Link
                        to="/rendez-vous"
                        state={{ serviceType: data.title }}
                        className="btn-gold"
                        data-testid={`doc-appointment-${d.key}`}
                      >
                        {t("common.bookAppointment")}
                        <ArrowRight className="w-4 h-4 rtl-flip" />
                      </Link>
                      <a
                        href={whatsappUrl(`${t("whatsappMessages.general")} (${data.title})`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline-dark"
                        data-testid={`doc-whatsapp-${d.key}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                        {t("common.whatsapp")}
                      </a>
                      <button
                        type="button"
                        onClick={() => window.print()}
                        className="btn-ghost"
                        data-testid={`doc-print-${d.key}`}
                      >
                        <Printer className="w-4 h-4" />
                        {t("common.print")}
                      </button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </section>
    </div>
  );
}
